import React from 'react';

// Inline SVG sparkline — values normalized to the box.
export function Sparkline({ data, width = 64, height = 26, tone = 'pink', bars = false }) {
	const colors = {
		pink: '#ec4899',
		purple: '#8b5cf6',
		cyan: '#22d3ee',
		green: '#4ade80',
		amber: '#fbbf24',
		blue: '#60a5fa',
		red: '#f87171',
	};
	const color = colors[tone] || colors.pink;
	const max = Math.max(...data);
	const min = Math.min(...data);
	const range = max - min || 1;

	if (bars) {
		const bw = width / data.length - 2;
		return (
			<svg width={width} height={height} aria-hidden="true">
				{data.map((v, i) => {
					const h = 3 + ((v - min) / range) * (height - 5);
					return (
						<rect
							key={i}
							x={i * (bw + 2)}
							y={height - h}
							width={bw}
							height={h}
							rx="1.5"
							fill={color}
							opacity={0.35 + (i / data.length) * 0.65}
						/>
					);
				})}
			</svg>
		);
	}

	const pts = data
		.map((v, i) => `${(i / (data.length - 1)) * width},${height - 2 - ((v - min) / range) * (height - 4)}`)
		.join(' ');
	return (
		<svg width={width} height={height} aria-hidden="true">
			<polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

export function StatCard({ label, value, trend, tone = 'pink', bars = false }) {
	return (
		<div className="cop-stat">
			<div className="cop-stat-label">{label}</div>
			<div className={`cop-stat-value tone-${tone}`}>{value}</div>
			{trend && <Sparkline data={trend} tone={tone} bars={bars} />}
		</div>
	);
}

export function Pill({ tone = 'dim', children }) {
	return <span className={`cop-pill ${tone}`}>{children}</span>;
}

const RISK_TONE = { low: 'green', medium: 'blue', high: 'amber', critical: 'red' };
export function RiskPill({ level }) {
	return <Pill tone={RISK_TONE[level] || 'dim'}>{level}</Pill>;
}

const STATUS_TONE = {
	active: 'green',
	live: 'green',
	monitoring: 'amber',
	building: 'blue',
	underperforming: 'red',
	paused: 'dim',
	archived: 'dim',
};
export function StatusPill({ status }) {
	return <Pill tone={STATUS_TONE[status] || 'dim'}>{status}</Pill>;
}

export function Confidence({ value }) {
	return (
		<span className="cop-conf">
			<span className="cop-conf-track">
				<span className="cop-conf-fill" style={{ width: `${Math.round(value * 100)}%` }} />
			</span>
			<span className="cop-num" style={{ fontSize: 11.5 }}>{Math.round(value * 100)}%</span>
		</span>
	);
}

export function Card({ title, icon = '◆', right, className = '', children, style }) {
	return (
		<section className={`cop-card ${className}`} style={style}>
			{title && (
				<h3 className="cop-card-title">
					<span className="spark">{icon}</span>
					{title}
					{right && <span className="right">{right}</span>}
				</h3>
			)}
			{children}
		</section>
	);
}

// Internal-only card: amber dashed border + INTERNAL tag. Render guarded by
// viewMode — these never appear in Client View.
export function InternalCard({ title, icon = '⚙', right, className = '', children, style }) {
	return (
		<section className={`cop-card cop-internal ${className}`} style={style}>
			<span className="cop-internal-tag">Internal</span>
			{title && (
				<h3 className="cop-card-title">
					<span className="spark" style={{ color: 'var(--amber)' }}>{icon}</span>
					{title}
					{right && <span className="right">{right}</span>}
				</h3>
			)}
			{children}
		</section>
	);
}

export function WarnRow({ level = 'warn', children }) {
	const icons = { error: '✕', warn: '!', ok: '✓' };
	return (
		<div className={`cop-warn-row ${level}`}>
			<span className="cop-warn-ico">{icons[level]}</span>
			<span>{children}</span>
		</div>
	);
}

// Link out to the AOP operations dashboard (separate route, full page load).
export function AopLink({ view, children }) {
	return (
		<a className="cop-aop-link" href={`/aop${view ? `?view=${view}` : ''}`} target="_blank" rel="noreferrer">
			{children} ↗
		</a>
	);
}

// Figma brand mark (the templates live in Figma). Aspect ratio ~0.8 (1024×1280).
export function FigmaLogo({ size = 14 }) {
	return (
		<svg width={size * 0.8} height={size} viewBox="0 0 1024 1280" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
			<path d="M312 840C312 784.772 356.772 740 412 740H512V840C512 895.228 467.228 940 412 940C356.772 940 312 895.228 312 840Z" fill="#24CB71" />
			<path d="M512 340V540H612C667.228 540 712 495.228 712 440C712 384.772 667.228 340 612 340H512Z" fill="#FF7237" />
			<path d="M611.167 740C666.395 740 711.167 695.228 711.167 640C711.167 584.772 666.395 540 611.167 540C555.939 540 511.167 584.772 511.167 640C511.167 695.228 555.939 740 611.167 740Z" fill="#00B6FF" />
			<path d="M312 440C312 495.228 356.772 540 412 540H512V340H412C356.772 340 312 384.772 312 440Z" fill="#FF3737" />
			<path d="M312 640C312 695.228 356.772 740 412 740H512V540H412C356.772 540 312 584.772 312 640Z" fill="#874FFF" />
		</svg>
	);
}

export function Toast({ children }) {
	return (
		<div className="cop-toast">
			<span style={{ color: 'var(--green)' }}>●</span>
			{children}
		</div>
	);
}

export function Modal({ title, sub, children, actions, onClose }) {
	return (
		<div className="cop-modal-backdrop" onClick={onClose}>
			<div className="cop-modal" onClick={(e) => e.stopPropagation()}>
				<h3>{title}</h3>
				{sub && <p>{sub}</p>}
				{children}
				<div className="cop-modal-actions">{actions}</div>
			</div>
		</div>
	);
}
